import { useForm } from "@tellescope/forms";
import { useVitalsQueryWithFilters } from "@tellescope/tanstack-query";
import type { MultiSelectOption } from "@tellescope/ui";
import { type ObservationType } from "@tellescope/utils";
import { useMemo, type Dispatch, type SetStateAction } from "react";
import { useVitalProgramsAndThresholds } from "../../../hooks";
import { useVitalsChart } from "./VitalsChart";

const MS_IN_DAY = 1000 * 60 * 60 * 24;

const OBSERVATION_TYPE_UNITS: Record<ObservationType, string[] | undefined> = {
  "blood-pressure": ["SBP", "DBP", "BPM"],
  weight: ["LB"],
  height: ["Height"],
  "heart-rate": ["BPM"],
  glucose: ["mg/dL"],
  unknown: undefined,
};

const MED_STATUS_VALUES: Record<string, string | undefined> = {
  all: undefined,
  before: "Medication: Before Meds",
  after: "Medication: After Meds",
};

const MEAL_STATUS_VALUES: Record<string, boolean | undefined> = {
  all: undefined,
  before: true,
  after: false,
};

export interface UseVitalsProgramSubsectionArgs {
  enduserId: string;
  observationType: ObservationType;
  unit?: string;
  observationId: string | undefined;
  setObservationId: Dispatch<SetStateAction<string | undefined>>;
}

export const useVitalsProgramSubsection = ({
  enduserId,
  observationType,
  unit,
  observationId,
  setObservationId,
}: UseVitalsProgramSubsectionArgs) => {
  const { thresholdsFromPrograms } = useVitalProgramsAndThresholds();

  const units = OBSERVATION_TYPE_UNITS[observationType];

  const selectedThresholdIds = useMemo(() => {
    if (!units) return undefined;
    const unitSet = new Set(units);
    return thresholdsFromPrograms.filter((threshold) => unitSet.has(threshold.unit)).map((threshold) => threshold.id);
  }, [thresholdsFromPrograms, units]);

  // Device options come from this contact's own observations, deliberately queried without the
  // `sources` filter applied: filtering by source would collapse the option list to whatever is
  // already selected. Reading the shared query cache instead would surface other contacts'
  // devices, since cached observation queries are not scoped per enduser.
  const deviceSourcesQuery = useVitalsQueryWithFilters({
    filters: {
      enduserIds: [enduserId],
      units: observationType === "unknown" && unit ? [unit] : units,
      validStatus: "valid",
    },
    combineRelatedObservations: false,
  });

  const filtersForm = useForm({
    fields: {
      dateRange: {
        type: "dateRange",
        initialValue: {
          from: undefined,
          to: undefined,
        },
      },
      meds: {
        type: "radio",
        initialValue: "all",
      },
      meal: {
        type: "radio",
        initialValue: "all",
      },
      devices: {
        type: "multiselect",
        initialValue: [],
      },
    },
  });

  const medStatus = MED_STATUS_VALUES[filtersForm.values.meds];
  const beforeMeal = MEAL_STATUS_VALUES[filtersForm.values.meal];

  const observationsQuery = useVitalsQueryWithFilters({
    limit: 50,
    filters: {
      enduserIds: [enduserId],
      units: observationType === "unknown" && unit ? [unit] : units,
      dateRange: {
        from: filtersForm.values.dateRange.from ? new Date(filtersForm.values.dateRange.from) : undefined,
        to: filtersForm.values.dateRange.to ? new Date(filtersForm.values.dateRange.to + MS_IN_DAY) : undefined,
      },
      sources: filtersForm.values.devices.length > 0 ? filtersForm.values.devices : undefined,
      ...(medStatus !== undefined && { medStatus }),
      ...(beforeMeal !== undefined && { beforeMeal }),
      validStatus: "valid",
    },
    combineRelatedObservations: true,
  });

  const deviceOptions = useMemo<MultiSelectOption<string>[]>(() => {
    const sources = new Set((deviceSourcesQuery.data ?? []).flatMap(({ source }) => (source ? [source] : [])));
    return Array.from(sources).map((name) => ({ label: name, value: name }));
  }, [deviceSourcesQuery.data]);

  const filteredObservations = useMemo(() => {
    if (!observationsQuery.data) return observationsQuery.data;

    switch (observationType) {
      case "blood-pressure": {
        const allowedUnits = new Set(["SBP", "DBP", "BPM"]);
        return observationsQuery.data.filter((obs) => {
          const obsUnits = new Set(obs.measurements.map((m) => m.unit));
          return obsUnits.has("SBP") && obsUnits.has("DBP") && [...obsUnits].every((u) => allowedUnits.has(u));
        });
      }
      case "heart-rate":
        return observationsQuery.data.filter((obs) => obs.measurements.every((m) => m.unit === "BPM"));
      case "weight":
      case "height":
      case "glucose":
      case "unknown":
        return observationsQuery.data;
    }
  }, [observationsQuery.data, observationType]);

  const vitalsChartProps = useVitalsChart({
    observations: filteredObservations,
    thresholds: thresholdsFromPrograms,
    selectedThresholdIds: selectedThresholdIds,
    observationType: observationType,
    observationId: observationId,
    setObservationId: setObservationId,
  });

  return {
    filtersForm,
    deviceOptions,
    filteredObservations,
    selectedThresholdIds,
    vitalsChartProps,
    fetchNextPage: observationsQuery.fetchNextPage,
  };
};
